import {
  Drawer,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Divider,
  IconButton,
  Box,
  Avatar,
  Typography,
  Tooltip,
} from '@mui/material';
import { useNavigate, useLocation } from 'react-router-dom';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import SettingsIcon from '@mui/icons-material/Settings';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import PeopleIcon from '@mui/icons-material/People';
import DescriptionIcon from '@mui/icons-material/Description';
import AssessmentIcon from '@mui/icons-material/Assessment';
import ErrorIcon from '@mui/icons-material/Error';
import LogoutIcon from '@mui/icons-material/Logout';
import { useState } from 'react';
import HomeIcon from '@mui/icons-material/Home';
import { useDispatch, useSelector } from 'react-redux';
import { clearUser } from '../store/userSlice';
import { UserRole } from '../types/user';
import kfintechLogo from '../assets/kfintech-logo.png';
import webileappsLogo from '../assets/webileapps.png';
import profileImage from '../assets/profile.png';

interface SidebarProps {
  onProfileClick?: () => void;
}

const SIDEBAR_OPEN_STORAGE_KEY = 'sidebar:open';

const readInitialOpen = (): boolean => {
  if (typeof window === 'undefined') return true;
  try {
    const raw = window.localStorage.getItem(SIDEBAR_OPEN_STORAGE_KEY);
    if (raw === null) return true;
    return JSON.parse(raw) === true;
  } catch {
    return true;
  }
};

export default function Sidebar({ onProfileClick }: SidebarProps) {
  const [open, setOpen] = useState<boolean>(readInitialOpen);

  const handleToggleOpen = () => {
    setOpen((prev) => {
      const next = !prev;
      try {
        window.localStorage.setItem(SIDEBAR_OPEN_STORAGE_KEY, JSON.stringify(next));
      } catch {
        // ignore storage failures (private mode, quota, etc.)
      }
      return next;
    });
  };

  const navigate = useNavigate();
  const location = useLocation();
  const dispatch = useDispatch();
  const userRole = useSelector((state: any) => state.user.role) as UserRole | undefined;
  const userName = useSelector((state: any) => state.user.name) as string | undefined;
  const isSupport = userRole === UserRole.SUPPORT;
  const isAdmin = userRole === UserRole.ADMIN;
  const isAmcAdmin = userRole === UserRole.AMC_ADMIN;
  const isAmcSupport = userRole === UserRole.AMC_SUPPORT;
  const isAMCUser = userRole === UserRole.AMC;

  const handleNavigation = (path: string) => {
    navigate(path);
  };

  const handleLogout = () => {
    dispatch(clearUser());
    navigate('/login');
  };

  const currentPath =
    location.pathname.startsWith('/payments')
      ? '/payments'
      : location.pathname.startsWith('/dashboard')
        ? '/dashboard'
        : location.pathname.startsWith('/reports')
          ? '/reports'
          : location.pathname.startsWith('/swift-flow-errors')
            ? '/swift-flow-errors'
            : location.pathname.startsWith('/bank-details')
              ? '/bank-details'
              : location.pathname.startsWith('/users')
                ? '/users'
                : '/amcs';

  return (
    <Drawer
      variant="permanent"
      open={open}
      sx={{
        width: open ? 240 : 56,
        flexShrink: 0,
        '& .MuiDrawer-paper': {
          width: open ? 240 : 56,
          boxSizing: 'border-box',
          transition: 'width 0.3s ease',
        },
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', p: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, overflow: 'hidden' }}>
          <Box sx={{ height: 24, display: 'flex', alignItems: 'center' }}>
            <img src={webileappsLogo} alt="Webileapps" style={{ height: '100%', width: 'auto' }} />
          </Box>
          {open && (
            <>
              <Divider orientation="vertical" flexItem sx={{ mx: 1 }} />
              <Box sx={{ height: 18, display: 'flex', alignItems: 'center' }}>
                <img src={kfintechLogo} alt="KFintech" style={{ height: '100%', width: 'auto' }} />
              </Box>
            </>
          )}
        </Box>
        <IconButton
          onClick={handleToggleOpen}
          size="small"
          aria-label={open ? 'Collapse sidebar' : 'Expand sidebar'}
        >
          {open ? <ChevronLeftIcon /> : <ChevronRightIcon />}
        </IconButton>
      </Box>
      <Divider />
      <List>
        <ListItem disablePadding>
          <Tooltip title={open ? '' : 'Dashboard'} placement="right" arrow disableInteractive>
            <ListItemButton
              selected={currentPath === '/dashboard'}
              onClick={() => handleNavigation('/dashboard')}
              sx={{
                px: 2.5,
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <HomeIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Dashboard" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        {!isAMCUser && (
          <ListItem disablePadding>
            <Tooltip title={open ? '' : 'Reports'} placement="right" arrow disableInteractive>
              <ListItemButton
                selected={currentPath === '/reports'}
                onClick={() => handleNavigation('/reports')}
                sx={{
                  px: 2.5,
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <AssessmentIcon />
                </ListItemIcon>
                {open && <ListItemText primary="Reports" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
        <ListItem disablePadding>
          <Tooltip title={open ? '' : 'Configuration'} placement="right" arrow disableInteractive>
            <ListItemButton
              selected={currentPath === '/amcs'}
              onClick={() => handleNavigation('/amcs')}
              sx={{
                px: 2.5,
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <SettingsIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Configuration" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        <ListItem disablePadding>
          <Tooltip title={open ? '' : 'Payments'} placement="right" arrow disableInteractive>
            <ListItemButton
              selected={currentPath === '/payments'}
              onClick={() => handleNavigation('/payments')}
              sx={{
                px: 2.5,
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <PaymentIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Payments" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        {(isAdmin || isAmcAdmin || isSupport || isAmcSupport) && (
          <ListItem disablePadding>
            <Tooltip title={open ? '' : 'Bank Details'} placement="right" arrow disableInteractive>
              <ListItemButton
                selected={currentPath === '/bank-details'}
                onClick={() => handleNavigation('/bank-details')}
                sx={{
                  px: 2.5,
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <AccountBalanceIcon />
                </ListItemIcon>
                {open && <ListItemText primary="Bank Details" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
        {isAdmin && (
          <ListItem disablePadding>
            <Tooltip title={open ? '' : 'Errors Master'} placement="right" arrow disableInteractive>
              <ListItemButton
                selected={currentPath === '/swift-flow-errors'}
                onClick={() => handleNavigation('/swift-flow-errors')}
                sx={{
                  px: 2.5,
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <ErrorIcon />
                </ListItemIcon>
                {open && <ListItemText primary="Errors Master" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
        {!isSupport && !isAMCUser && (
          <ListItem disablePadding>
            <Tooltip title={open ? '' : 'Users'} placement="right" arrow disableInteractive>
              <ListItemButton
                selected={currentPath === '/users'}
                onClick={() => handleNavigation('/users')}
                sx={{
                  px: 2.5,
                  minHeight: 48,
                  justifyContent: open ? 'initial' : 'center',
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: 0,
                    mr: open ? 3 : 'auto',
                    justifyContent: 'center',
                  }}
                >
                  <PeopleIcon />
                </ListItemIcon>
                {open && <ListItemText primary="Users" />}
              </ListItemButton>
            </Tooltip>
          </ListItem>
        )}
        {isAdmin && (
        <ListItem disablePadding>
          <Tooltip title={open ? '' : 'Docs'} placement="right" arrow disableInteractive>
            <ListItemButton
              onClick={() => {
                window.open('/docs', '_blank', 'noopener,noreferrer');
              }}
              sx={{
                px: 2.5,
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <DescriptionIcon />
              </ListItemIcon>
              {open && <ListItemText primary="Docs" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
        )}
      </List>
      <Box sx={{ flexGrow: 1 }} />
      {/* Profile section */}
      <Box sx={{ px: 1.5, pb: 1 }}>
        <Tooltip
          title={open ? '' : userName || 'Admin'}
          placement="right"
          arrow
          disableInteractive
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              p: 1,
              borderRadius: 1,
              cursor: 'pointer',
              justifyContent: open ? 'flex-start' : 'center',
              '&:hover': {
                backgroundColor: 'rgba(0, 0, 0, 0.04)',
              }
            }}
            onClick={onProfileClick}
          >
            <Avatar src={profileImage} sx={{ width: 32, height: 32 }} />
            {open && (
              <Typography variant="body2" noWrap>
                {userName || 'Admin'}
              </Typography>
            )}
          </Box>
        </Tooltip>
      </Box>
      <Divider />
      <List>
        <ListItem disablePadding>
          <Tooltip title={open ? '' : 'Logout'} placement="right" arrow disableInteractive>
            <ListItemButton
              onClick={handleLogout}
              sx={{
                px: 2.5,
                minHeight: 48,
                justifyContent: open ? 'initial' : 'center',
              }}
            >
              <ListItemIcon
                sx={{
                  minWidth: 0,
                  mr: open ? 3 : 'auto',
                  justifyContent: 'center',
                }}
              >
                <LogoutIcon color="error" />
              </ListItemIcon>
              {open && <ListItemText primary="Logout" />}
            </ListItemButton>
          </Tooltip>
        </ListItem>
      </List>
    </Drawer>
  );
}
